import { getCustomDocumentType } from '@/components/tiptap-editor/tiptap-extension/custom-document-extension/custom-document-content-plugin';
import { useTiptapEditor } from '@/hooks/use-tiptap-editor';
import { isSelectionTopNode } from '@/lib/zy-tiptap-utils';
import type { Editor } from '@tiptap/core';
import { FlaskRound } from 'lucide-react';
import { forwardRef, useCallback, useSyncExternalStore } from 'react';
import { GenericExtensionButton, type GenericExtensionButtonProps } from '../generic-extension-button';

function isTestQuestionDocument(editor: Editor): boolean {
    return getCustomDocumentType(editor.state.doc) === 'test_question';
}

const ZyTestQuestionButton = forwardRef<
    HTMLButtonElement,
    Omit<GenericExtensionButtonProps, 'extensionName' | 'extensionType' | 'icon' | 'label' | 'command'>
>((props, ref) => {
    const { editor } = useTiptapEditor(props.editor);

    const subscribe = useCallback(
        (onStoreChange: () => void) => {
            if (!editor) {
                return () => {};
            }
            editor.on('update', onStoreChange);
            return () => editor.off('update', onStoreChange);
        },
        [editor],
    );

    const getSnapshot = useCallback(() => (editor ? isTestQuestionDocument(editor) : false), [editor]);

    const visible = useSyncExternalStore(subscribe, getSnapshot);

    if (!visible) {
        return null;
    }

    return (
        <GenericExtensionButton
            extensionName="testQuestion"
            extensionType="node"
            icon={FlaskRound}
            isDisabled={editor => !isSelectionTopNode(editor)}
            label="Insert test question"
            command={editor => editor.commands.insertTestQuestion()}
            ref={ref}
            {...props}
        />
    );
});

ZyTestQuestionButton.displayName = 'ZyTestQuestionButton';

export default ZyTestQuestionButton;
